import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';

import { StaticContext, TabletScreen, MobileScreen } from '@sm/webassets';
import { defineMessages } from '@sm/intl';
import { Typography, Accordion, AccordionItem, List, ListItem } from '@sm/wds-react';
import { getClientEnvironmentDetails } from '@sm/utils';

import './navbar.scss';
import publishedApplicationListingCategoriesQuery from './NavBar.graphql';
import SidebarSkeleton from './SideBarSkeleton';
// LinkWithSearch should be used so it will keep any existing query arguments present in the url (needed for ut_source)
import LinkWithSearch from '../LinkWithSearch';

export const COPY = defineMessages({
  NAVBAR_APPS_DIRECTORY: {
    id: 'AppsDirectory.NavBar.AppsDirectory',
    defaultMessage: 'App Directory',
    description: `[Type: Header][Vis.: high] - A section title for a group of links on the page's sidebar`,
  },
  NAVBAR_CATEGORIES: {
    id: 'AppsDirectory.NavBar.Categories',
    defaultMessage: 'App Category',
    description: `[Type: Header][Vis.: high] - A section title for a group of links on the page's sidebar`,
  },
  NAVBAR_MY_APPS: {
    id: 'AppsDirectory.NavBar.MyApps',
    defaultMessage: 'My Apps',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user to its installed apps`,
  },
  NAVBAR_FEATURED: {
    id: 'AppsDirectory.NavBar.Featured',
    defaultMessage: 'Featured',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user the featured apps`,
  },
  NAVBAR_MOST_POPULAR: {
    id: 'AppsDirectory.NavBar.MostPopular',
    defaultMessage: 'Most Popular',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user to the most popular apps`,
  },
  NAVBAR_RECENTLY_ADDED: {
    id: 'AppsDirectory.NavBar.RecentlyAdded',
    defaultMessage: 'Recently Added',
    description: `[Type: Link][Vis.: med] - A link on the sidebar directing the user to the most recently added apps`,
  },
});

const SidebarLink = ({ target, text, 'data-testid': dataTestId }) => (
  <li className="sm-navbar__group-item" data-testid={dataTestId}>
    <LinkWithSearch pathname={target} className="sm-navbar__group-item-link">
      {text}
    </LinkWithSearch>
  </li>
);

SidebarLink.propTypes = {
  target: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  'data-testid': PropTypes.string,
};

SidebarLink.defaultProps = {
  'data-testid': 'NavBar__SidebarLink',
};

const NavBar = props => {
  const {
    environment: { languageId },
    user,
    userAgent,
  } = useContext(StaticContext);
  const { isDesktop } = getClientEnvironmentDetails(userAgent);
  return (
    <div {...props}>
      <Query query={publishedApplicationListingCategoriesQuery} variables={{ language: languageId }}>
        {({ loading, data, error }) => {
          return (
            <>
              <MobileScreen defaultMatches={!isDesktop}>
                <Accordion className="wds-m-b-4 sm-navbar__accordion">
                  <AccordionItem dense title={COPY.NAVBAR_APPS_DIRECTORY.defaultMessage}>
                    <List dense transparent>
                      <Choose>
                        <When condition={user.isAuthenticated}>
                          <ListItem>
                            <LinkWithSearch pathname="/apps/my-apps">
                              <Typography>{COPY.NAVBAR_MY_APPS.defaultMessage}</Typography>
                            </LinkWithSearch>
                          </ListItem>
                        </When>
                        <Otherwise>
                          <span />
                        </Otherwise>
                      </Choose>
                      <ListItem>
                        <LinkWithSearch pathname="/apps/featured-apps">
                          <Typography>{COPY.NAVBAR_FEATURED.defaultMessage}</Typography>
                        </LinkWithSearch>
                      </ListItem>
                      <ListItem>
                        <LinkWithSearch pathname="/apps/most-popular">
                          <Typography>{COPY.NAVBAR_MOST_POPULAR.defaultMessage}</Typography>
                        </LinkWithSearch>
                      </ListItem>
                      <ListItem>
                        <LinkWithSearch pathname="/apps/recently-added">
                          <Typography>{COPY.NAVBAR_RECENTLY_ADDED.defaultMessage}</Typography>
                        </LinkWithSearch>
                      </ListItem>
                    </List>
                  </AccordionItem>
                  <AccordionItem title={COPY.NAVBAR_CATEGORIES.defaultMessage}>
                    <Choose>
                      <When condition={!loading && data}>
                        <List transparent>
                          {data.publishedApplicationListingCategories.map(cat => (
                            <ListItem key={cat.id}>
                              <LinkWithSearch pathname={`/apps/categories/${cat.key}`}>
                                <Typography>{cat.name}</Typography>
                              </LinkWithSearch>
                            </ListItem>
                          ))}
                        </List>
                      </When>
                      <Otherwise>
                        <span />
                      </Otherwise>
                    </Choose>
                  </AccordionItem>
                </Accordion>
              </MobileScreen>
              <TabletScreen defaultMatches={isDesktop}>
                <Typography component="div" variant="cardTitle" className="sm-navbar__title">
                  {COPY.NAVBAR_APPS_DIRECTORY.defaultMessage}
                </Typography>
                <ul className="sm-navbar__group wds-m-t-3 wds-m-b-3">
                  <If condition={user.isAuthenticated}>
                    <SidebarLink target="/apps/my-apps" text={COPY.NAVBAR_MY_APPS.defaultMessage} />
                  </If>
                  <SidebarLink target="/apps/featured-apps" text={COPY.NAVBAR_FEATURED.defaultMessage} />
                  <SidebarLink target="/apps/most-popular" text={COPY.NAVBAR_MOST_POPULAR.defaultMessage} />
                  <SidebarLink target="/apps/recently-added" text={COPY.NAVBAR_RECENTLY_ADDED.defaultMessage} />
                </ul>
                <Typography component="div" variant="cardTitle" className="sm-navbar__title">
                  {COPY.NAVBAR_CATEGORIES.defaultMessage}
                </Typography>
                <Choose>
                  <When condition={loading}>
                    <SidebarSkeleton />
                  </When>
                  <When condition={error}>null</When>
                  <Otherwise>
                    <ul className="sm-navbar__group sm-navbar__categories">
                      {data.publishedApplicationListingCategories.map(cat => (
                        <SidebarLink
                          data-testid="NavBar__CategoryItem"
                          key={cat.id}
                          target={`/apps/categories/${cat.key}`}
                          text={cat.name}
                        />
                      ))}
                    </ul>
                  </Otherwise>
                </Choose>
              </TabletScreen>
            </>
          );
        }}
      </Query>
    </div>
  );
};

export default NavBar;
